import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CameraIcon, CheckIcon, ImageIcon, MapPinIcon, PhoneIcon, SparklesIcon, TriangleAlertIcon } from 'lucide-react';
import { intents } from '../../data/demandes';
import type { AtnaTurnKind, FollowUpInfo, Intent } from '../../types/demandes';
import { LocationPicker } from './LocationPicker';
import { NotifyFollowUp } from './NotifyFollowUp';

/** Adresse « détectée » par la géolocalisation simulée. */
const DETECTED_ADDRESS = 'Rue Elm';
interface AtnaMessageProps {
  kind: AtnaTurnKind;
  /** Only the last assistant turn keeps its actions; history is inert. */
  live: boolean;
  intent: Intent;
  address: string;
  description: string;
  answer: string | null;
  hasPhoto: boolean;
  onConfirmIntent: () => void;
  onPickIntent: (intent: Intent) => void;
  onNotEmergency: () => void;
  onPickAddress: (address: string, source: 'position' | 'profil') => void;
  onAddPhoto: () => void;
  onSkipPhoto: () => void;
  onAnswer: (answer: string) => void;
  onSend: () => void;
  onEdit: () => void;
  onReset: () => void;
  /** Raccourci choisi après « Comment puis-je vous aider ? ». */
  onPickHelpTopic: (label: string) => void;
  /** Choix rapide du problème quand le lieu vient de la carte. */
  onPickProblem: (label: string) => void;
  /** Requête existante suivie (lampadaire). */
  followUp: FollowUpInfo | null;
  onNewProblem: () => void;
}

/** Problèmes proposés quand le lieu a été choisi sur la carte. */
const mapProblems = ['Nid-de-poule', 'Éclairage', 'Arbre', 'Eau'];
const primaryButton = 'rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2';
const chipButton = 'rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const quietButton = 'text-sm font-semibold text-slate-500 underline underline-offset-4 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
export function AtnaMessage({
  kind,
  live,
  intent,
  address,
  description,
  answer,
  hasPhoto,
  onConfirmIntent,
  onPickIntent,
  onNotEmergency,
  onPickAddress,
  onAddPhoto,
  onSkipPhoto,
  onAnswer,
  onSend,
  onEdit,
  onReset,
  onPickHelpTopic,
  onPickProblem,
  followUp,
  onNewProblem
}: AtnaMessageProps) {
  if (kind === 'suivi' && followUp) {
    return <div data-id="element-1084" data-mp="gzojplis10c8">
        <p className={eyebrow} data-id="element-1085" data-mp="gzycthz4bnm5">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-1086" data-mp="gyn4r5yn3tp8" />
          Vous avez déjà une demande à ce sujet
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-1087" data-mp="eg1oc3xxalx1">
          <p className="text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-1088" data-mp="ee7d5hcge865">{followUp.title}</p>
          <span className="rounded-full bg-ville-indigo-soft px-2.5 py-0.5 text-xs font-bold text-ville-indigo" data-id="element-1089" data-mp="9fdyvmpced4x">
            En traitement
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-500" data-id="element-1090" data-mp="fz80m59k0jw">
          {followUp.reference} · {followUp.address}
        </p>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-600" data-id="element-1091" data-mp="3ro4t7udq0pe9">
          Votre demande est toujours en cours : une intervention est planifiée par la Ville.
        </p>

        {followUp.question && <div className="mt-4 rounded-2xl bg-ville-sand p-4" data-id="element-1092" data-mp="3hunv4e6jwo4s">
            <p className="text-sm font-extrabold text-ville-navy" data-id="element-1093" data-mp="3cz8hnncggu8w">La Ville a besoin d’une précision</p>
            <p className="mt-1.5 text-[15px] leading-relaxed text-ville-navy" data-id="element-1094" data-mp="38vspb37f5g20">« {followUp.question} »</p>
            {live && <p className="mt-2 text-xs text-slate-500" data-id="element-1095" data-mp="33zli773adyih">
                Répondez simplement ci-dessous, je transmets votre réponse dans les messages de la requête.
              </p>}
          </div>}

        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-1096" data-mp="y52wpz6zvamp">
            <Link to={`/mes-demandes/${followUp.id}`} className={chipButton} data-id="element-1097" data-mp="133e5e6ljzf3c">
              Voir la requête
            </Link>
            <button type="button" onClick={onNewProblem} className={quietButton} data-id="element-1098" data-mp="o5u5vf7fec4k">
              C’est un autre problème
            </button>
          </div>}
      </div>;
  }
  if (kind === 'suivi-merci' && followUp) {
    return <div data-id="element-1099" data-mp="f79wx5ro3gtj">
        <p className={eyebrow} data-id="element-1100" data-mp="f7ry8gt6sl8b">
          <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1101" data-mp="k409hj49jua7" />
          Réponse transmise
        </p>
        <p className="mt-2 text-[17px] font-bold leading-snug text-ville-navy" data-id="element-1102" data-mp="29iwv929rpzeo">
          Merci ! Votre précision a été ajoutée aux messages de la requête {followUp.reference}.
        </p>
        <p className="mt-1.5 text-[15px] leading-relaxed text-slate-600" data-id="element-1103" data-mp="2om82vn370feq">
          La Ville poursuit le traitement. Vous serez avisée à la prochaine étape.
        </p>
        {live && <Link to={`/mes-demandes/${followUp.id}?onglet=messages`} className={`${primaryButton} mt-4 inline-flex items-center gap-2`} data-id="element-1104" data-mp="2xerz6gw0i7we">
            Voir les messages
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1105" data-mp="37cpiqr2xfgio" />
          </Link>}
      </div>;
  }
  if (kind === 'aide') {
    return <div data-id="element-1106" data-mp="32dcvw6c229g3">
        <p className="text-[17px] font-bold text-ville-navy" data-id="element-1107" data-mp="32fz50aelgx0f">Comment puis-je vous aider ?</p>
        {live && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1108" data-mp="3gbb9vamgs3v2">
            {mapProblems.map(problem => <li key={problem} data-id="element-1109" data-mp="3ca7x4wzpqny2">
                <button type="button" onClick={() => onPickHelpTopic(problem)} className={chipButton} data-id="element-1110" data-mp="1lyuw7orc9ng6">
                  {problem}
                </button>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'quoi') {
    return <div data-id="element-1111" data-mp="3m1w4iuz9a1t3">
        <p className={eyebrow} data-id="element-1112" data-mp="3m1mej7e7nune">
          <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-1113" data-mp="3m2nmh2zxk10w" />
          Lieu sélectionné sur la carte
        </p>
        <p className="mt-2 text-[15px] font-semibold text-ville-navy" data-id="element-1114" data-mp="3qze4vwz7j8ph">{address}</p>
        <p className="mt-3 text-[17px] font-bold text-ville-navy" data-id="element-1115" data-mp="3w0todklql75r">Que voulez-vous signaler à cet endroit ?</p>
        {live && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1116" data-mp="3u6od0n6ws5p">
            {mapProblems.map(problem => <li key={problem} data-id="element-1117" data-mp="8t9fria8yzv7">
                <button type="button" onClick={() => onPickProblem(problem)} className={chipButton} data-id="element-1118" data-mp="dmca3smumdcg">
                  {problem}
                </button>
              </li>)}
          </ul>}
        {live && <p className="mt-3 text-xs text-slate-500" data-id="element-1119" data-mp="nljq6nkywm39">Autre chose ? Décrivez-le simplement dans le champ ci-dessous.</p>}
      </div>;
  }
  if (kind === 'urgence') {
    return <div className="border-l-4 border-red-600 pl-4" data-id="element-1120" data-mp="2c4xn8x4y9y3j">
        <p className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white" data-id="element-1121" data-mp="2c1jnzxrl250b">
          <TriangleAlertIcon className="h-4 w-4" aria-hidden="true" data-id="element-1122" data-mp="23497nxzwrpoj" />
          Urgence possible
        </p>
        <p className="mt-3 text-lg font-extrabold leading-snug text-ville-navy" data-id="element-1123" data-mp="1y5erl3tbsf84">
          Cette situation pourrait nécessiter une intervention immédiate.
        </p>
        <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-1124" data-mp="2qs8ysz14ti3n">
          N’attendez pas le traitement d’une demande en ligne. Composez le 911 si une personne est en danger, ou la
          ligne d’urgence municipale pour un bris dangereux sur la voie publique.
        </p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-1125" data-mp="3eiv80tmvh8f8">
            <a href="tel:911" className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-1126" data-mp="3ek6bstgbig6c">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1127" data-mp="2mvts4uxh8u01" />
              Appeler le 911
            </a>
            <a href="tel:5144285999" className="inline-flex items-center gap-2 rounded-full border border-ville-navy px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-navy hover:text-white" data-id="element-1128" data-mp="294onfef95biu">
              Ligne d’urgence municipale
            </a>
            <button type="button" onClick={onNotEmergency} className={quietButton} data-id="element-1129" data-mp="35ra2kq4pvb64">
              Ce n’est pas une urgence, continuer
            </button>
          </div>}
      </div>;
  }
  if (kind === 'comprehension') {
    return <div data-id="element-1130" data-mp="1gdohgxxd1xoj">
        <p className={eyebrow} data-id="element-1131" data-mp="1gd50pkjg5fc3">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-1132" data-mp="1gh2l09hrgcxk" />
          J’ai compris :
        </p>
        <p className="mt-2 text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-1133" data-mp="1lbgcw1x7s1xh">{intent.label}</p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-1134" data-mp="18vgxyfaj4q19">{intent.service}</p>
        <p className="mt-3 text-[15px] text-slate-600" data-id="element-1135" data-mp="18x9uv2vcbvw2">Est-ce bien le problème que vous souhaitez signaler ?</p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-2.5" data-id="element-1136" data-mp="1hw52mje19e1h">
            <button type="button" onClick={onConfirmIntent} className={primaryButton} data-id="element-1137" data-mp="1htsj41smmvmb">
              Oui
            </button>
            {intents.filter(option => option.label !== intent.label).slice(0, 3).map(option => <button key={option.label} type="button" onClick={() => onPickIntent(option)} className={chipButton} data-id="element-1138" data-mp="13xot6kosd9fn">
                  {option.label}
                </button>)}
          </div>}
      </div>;
  }
  if (kind === 'lieu') {
    return <div data-id="element-1139" data-mp="6hhxqgg7q1rw">
        <p className={eyebrow} data-id="element-1140" data-mp="6fx54wdgkswo">
          <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-1141" data-mp="6em2izvr1r75" />
          Où se trouve le problème ?
        </p>
        {live ? <LocationPicker onPick={onPickAddress} detectedAddress={DETECTED_ADDRESS} data-id="element-1142" data-mp="2mcpeg3r7ekzc" /> : <p className="mt-2 text-lg font-extrabold text-ville-navy" data-id="element-1143" data-mp="2hctjeu0hi3vx">{address}</p>}
      </div>;
  }
  if (kind === 'photo') {
    return <div data-id="element-1144" data-mp="2ce04qy9go6u6">
        <p className={eyebrow} data-id="element-1145" data-mp="2cgcj71g0fz2h">
          <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-1146" data-mp="28d6g22o72o08" />
          Photo
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1147" data-mp="23m7xsxjnbfuc">
          Une photo pourrait aider l’équipe à traiter votre demande.
        </p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-1148" data-mp="1ykqy35cwdzfc">
            <button type="button" onClick={onAddPhoto} className={`inline-flex items-center gap-2 ${primaryButton}`} data-id="element-1149" data-mp="1tp39ar2bv8ad">
              <CameraIcon className="h-4 w-4" aria-hidden="true" data-id="element-1150" data-mp="1onw14qw9w736" />
              Ajouter une photo
            </button>
            <button type="button" onClick={onSkipPhoto} className={quietButton} data-id="element-1151" data-mp="1kpg6c0ty9uf7">
              Continuer sans photo
            </button>
          </div>}
      </div>;
  }
  if (kind === 'detail') {
    return <div data-id="element-1152" data-mp="95zhtt286kof">
        <p className={eyebrow} data-id="element-1153" data-mp="957sv134vl74">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-1154" data-mp="9698liv3gvv6" />
          Une dernière précision
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1155" data-mp="e41a94ljjwck">{intent.question}</p>
        {live && <ul className="mt-4 flex flex-wrap items-start gap-2" data-id="element-1156" data-mp="3ckdbqbeqdhkz">
            {intent.answers.map(option => {
          // Simulation : ATNA « estime » la taille à partir de la photo jointe.
          const detected = hasPhoto && intent.label === 'Nid-de-poule' && option.startsWith('Moyen');
          return <li key={option} className="flex flex-col items-center" data-id="element-1157" data-mp="2tw8cnh90qksi">
                  <button type="button" onClick={() => onAnswer(option)} aria-describedby={detected ? 'taille-detectee' : undefined} className={`${chipButton} ${detected ? '!border-ville-indigo' : ''}`} data-id="element-1158" data-mp="2ttuj5ctzlgn2">
                    {option}
                  </button>
                  {detected && <span id="taille-detectee" className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-ville-indigo" data-id="element-1159" data-mp="30m9luk5xx01u">
                      <SparklesIcon className="h-3 w-3" aria-hidden="true" data-id="element-1160" data-mp="27xki0ddi2gme" />
                      Taille détectée
                    </span>}
                </li>;
        })}
          </ul>}
      </div>;
  }
  if (kind === 'revue') {
    return <div data-id="element-1161" data-mp="2ct9lgrsn4hnz">
        <p className="text-sm font-bold uppercase tracking-wider text-ville-indigo" data-id="element-1162" data-mp="2cwxfrt5wt83r">Votre demande est prête</p>
        <h3 className="mt-2 text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-1163" data-mp="3o9q5kyf53mh6">{intent.label}</h3>

        <dl className="mt-4 space-y-3 text-sm" data-id="element-1164" data-mp="1a28ija5pxamf">
          <div className="flex gap-3" data-id="element-1165" data-mp="1a0f9xpbjogbs">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-1166" data-mp="1f11qyo65ebfq">Lieu</dt>
            <dd className="flex items-center gap-1.5 font-semibold text-ville-navy" data-id="element-1167" data-mp="1f2cymz2ps3c4">
              <MapPinIcon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-1168" data-mp="112v3m1ytrtub" />
              {address}
            </dd>
          </div>
          {answer && <div className="flex gap-3" data-id="element-1169" data-mp="1tpfwx7yv2g0i">
              <dt className="w-24 shrink-0 text-slate-500" data-id="element-1170" data-mp="1tqhg5zq168tb">
                {intent.label === 'Nid-de-poule' ? 'Taille' : 'Précision'}
              </dt>
              <dd className="font-semibold text-ville-navy" data-id="element-1171" data-mp="1k0o99c0u2hve">{answer}</dd>
            </div>}
          <div className="flex gap-3" data-id="element-1172" data-mp="1oypnnjnbdt0j">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-1173" data-mp="1oxoejkpuzglg">Photo</dt>
            <dd className="font-semibold text-ville-navy" data-id="element-1174" data-mp="2cojzpyzt7djq">{hasPhoto ? '1 photo jointe' : 'Aucune photo'}</dd>
          </div>
        </dl>

        {live && <div className="mt-5 flex flex-wrap items-center gap-4" data-id="element-1175" data-mp="2hhd7ywuhl40k">
            <button type="button" onClick={onSend} className={primaryButton} data-id="element-1176" data-mp="svaijijtdsmy">
              Envoyer ma demande
            </button>
            <button type="button" onClick={onEdit} className={quietButton} data-id="element-1177" data-mp="nwz7j4hs9c35">
              Modifier
            </button>
          </div>}
      </div>;
  }
  return <div data-id="element-1178" data-mp="evsp2pvkpr79">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-1179" data-mp="ew13im7ch4kk">
        <CheckIcon className="h-6 w-6" data-id="element-1180" data-mp="17idhltnsc9ce" />
      </span>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1" data-id="element-1181" data-mp="17kzm1kfdq8qe">
        <h3 className="text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-1182" data-mp="12q2fck5kobgo">Demande transmise</h3>
        <span className="text-sm font-semibold text-slate-500" data-id="element-1183" data-mp="xqpuu626bgh9">#REQ-2842</span>
      </div>
      <NotifyFollowUp live={live} requestId="req-2842" data-id="element-1184" data-mp="1v9ivi3a60rk2" />
      {live && <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-1185" data-mp="1qgpot9v4z95c">
          <Link to="/mes-demandes/req-2842" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-1186" data-mp="1xsw220lpn4v5">
            Suivre ma demande
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1187" data-mp="2bsf369g3tfxy" />
          </Link>
          <button type="button" onClick={onReset} className={chipButton} data-id="element-1188" data-mp="2bq2gxk3bcjf3">
            Signaler autre chose
          </button>
        </div>}
    </div>;
}